import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Bell, Copy, Check, ChevronRight,
  UserCheck, UserX, ClipboardList, Pencil,
} from "lucide-react";
import apiFetch from "../apiFetch";
import "../styles/classroomPage.css";
import PageHeader from "../components/PageHeader";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ClassroomPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard]         = useState(null);
  const [students, setStudents]           = useState([]);
  const [requests, setRequests]           = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [tab, setTab]                     = useState("students"); // "students" | "requests" | "notifications"
  const [status, setStatus]               = useState("loading");

  // Code editing
  const [editingCode, setEditingCode]   = useState(false);
  const [codeInput, setCodeInput]       = useState("");
  const [codeError, setCodeError]       = useState("");
  const [codeSaving, setCodeSaving]     = useState(false);
  const [codeCopied, setCodeCopied]     = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, stuRes, reqRes, notifRes] = await Promise.all([
        apiFetch("/api/teacher/dashboard"),
        apiFetch("/api/teacher/students"),
        apiFetch("/api/teacher/requests"),
        apiFetch("/api/teacher/notifications"),
      ]);
      if (dashRes.status === 401 || dashRes.status === 403) { setStatus("denied"); return; }
      if (!dashRes.ok) { setStatus("error"); return; }
      const [dash, stu, reqs, notifs] = await Promise.all([
        dashRes.json(), stuRes.json(), reqRes.json(), notifRes.json(),
      ]);
      setDashboard(dash);
      setStudents(Array.isArray(stu) ? stu : []);
      setRequests(Array.isArray(reqs) ? reqs : []);
      setNotifications(Array.isArray(notifs) ? notifs : []);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    document.title = "My Class — Owllet Reading Quest";
    fetchAll();
  }, [fetchAll]);

  // Switch to requests tab if there are pending requests
  useEffect(() => {
    if (requests.length > 0 && tab === "students") setTab("requests");
  }, [requests.length, tab]);

  const handleAccept = async (connId) => {
    await apiFetch(`/api/teacher/requests/${connId}/accept`, { method: "POST" });
    fetchAll();
  };

  const handleDecline = async (connId) => {
    await apiFetch(`/api/teacher/requests/${connId}`, { method: "DELETE" });
    fetchAll();
  };

  const handleDisconnect = async (studentId) => {
    if (!window.confirm("Remove this student from your class?")) return;
    await apiFetch(`/api/teacher/students/${studentId}/disconnect`, { method: "DELETE" });
    fetchAll();
  };

  const handleMarkAllRead = async () => {
    await apiFetch("/api/teacher/notifications/read-all", { method: "PUT" });
    setNotifications(n => n.map(x => ({ ...x, is_read: 1 })));
    setDashboard(d => d ? { ...d, unreadCount: 0 } : d);
  };

  const startEditCode = () => {
    setCodeInput(dashboard?.code || "");
    setCodeError("");
    setEditingCode(true);
  };

  const saveCode = async () => {
    if (!codeInput.trim()) return;
    setCodeSaving(true);
    setCodeError("");
    try {
      const res = await apiFetch("/api/teacher/code", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: codeInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setCodeError(data.error || "Failed to save code"); setCodeSaving(false); return; }
      setDashboard(d => ({ ...d, code: data.code }));
      setEditingCode(false);
    } catch {
      setCodeError("Server error");
    }
    setCodeSaving(false);
  };

  const copyCode = () => {
    if (!dashboard?.code) return;
    navigator.clipboard.writeText(dashboard.code).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  if (status === "loading") {
    return (
      <div className="cls-page">
        <div className="cls-loading">Loading your classroom…</div>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="cls-page">
        <div className="cls-error-card">
          <p>This page is for teacher accounts only.</p>
          <Link to="/" className="cls-btn cls-btn--primary">Go Home</Link>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="cls-page">
        <div className="cls-error-card">
          <p>Failed to load classroom. Please try again.</p>
          <button className="cls-btn cls-btn--primary" onClick={fetchAll}>Retry</button>
        </div>
      </div>
    );
  }

  const unread = notifications.filter(n => !n.is_read).length;

  return (
    <div className="cls-page">
      <PageHeader
        pageTitle="My Class"
        pageSubtitle="Manage your students, share your class code and set assignments — all in one place"
      />

      <main className="cls-main">
        {/* Summary cards */}
        <div className="cls-summary-row">
          <div className="cls-summary-card cls-summary-card--purple">
            <Users size={24} />
            <div>
              <p className="cls-summary-val">{dashboard?.studentCount ?? 0}</p>
              <p className="cls-summary-lbl">Students</p>
            </div>
          </div>
          <div className="cls-summary-card cls-summary-card--amber">
            <ClipboardList size={24} />
            <div>
              <p className="cls-summary-val">{dashboard?.pendingCount ?? 0}</p>
              <p className="cls-summary-lbl">Pending Requests</p>
            </div>
          </div>
          <div className="cls-summary-card cls-summary-card--red">
            <Bell size={24} />
            <div>
              <p className="cls-summary-val">{dashboard?.unreadCount ?? 0}</p>
              <p className="cls-summary-lbl">Unread Notifications</p>
            </div>
          </div>
        </div>

        {/* Class code box */}
        <section className="cls-code-section">
          <div className="cls-code-label">Your Class Code</div>
          {editingCode ? (
            <div className="cls-code-edit-row">
              <input
                className="cls-code-input"
                value={codeInput}
                onChange={e => { setCodeInput(e.target.value.toUpperCase()); setCodeError(""); }}
                maxLength={30}
                autoFocus
                placeholder="e.g. SMITH-1234"
              />
              <button className="cls-btn cls-btn--primary cls-btn--sm" onClick={saveCode} disabled={codeSaving}>
                {codeSaving ? "Saving…" : "Save"}
              </button>
              <button className="cls-btn cls-btn--ghost cls-btn--sm" onClick={() => setEditingCode(false)}>Cancel</button>
              {codeError && <p className="cls-code-err">{codeError}</p>}
            </div>
          ) : (
            <div className="cls-code-display-row">
              <span className="cls-code-value">{dashboard?.code || "—"}</span>
              <button className="cls-btn cls-btn--ghost cls-btn--icon cls-btn--sm" onClick={copyCode} title="Copy code">
                {codeCopied ? <Check size={16} color="#22c55e" /> : <Copy size={16} />}
              </button>
              <button className="cls-btn cls-btn--ghost cls-btn--icon cls-btn--sm" onClick={startEditCode} title="Edit code">
                <Pencil size={16} />
              </button>
            </div>
          )}
          <p className="cls-code-hint">Share this code with students so they can join your class.</p>
        </section>

        {/* Tabs */}
        <div className="cls-tabs">
          <button
            className={`cls-tab${tab === "students" ? " cls-tab--active" : ""}`}
            onClick={() => setTab("students")}
          >
            <Users size={16} /> Students ({students.length})
          </button>
          <button
            className={`cls-tab${tab === "requests" ? " cls-tab--active" : ""}`}
            onClick={() => setTab("requests")}
          >
            <UserCheck size={16} /> Requests
            {requests.length > 0 && <span className="cls-tab-badge">{requests.length}</span>}
          </button>
          <button
            className={`cls-tab${tab === "notifications" ? " cls-tab--active" : ""}`}
            onClick={() => setTab("notifications")}
          >
            <Bell size={16} /> Notifications
            {unread > 0 && <span className="cls-tab-badge">{unread}</span>}
          </button>
        </div>

        {/* Tab: Students */}
        {tab === "students" && (
          <div className="cls-panel">
            {students.length === 0 ? (
              <div className="cls-empty">
                <Users size={40} className="cls-empty-icon" />
                <p>No students connected yet.</p>
                <p className="cls-empty-sub">Share your class code and accept requests to add students.</p>
              </div>
            ) : (
              <ul className="cls-student-list">
                {students.map(s => (
                  <li key={s.student_id} className="cls-student-row">
                    <div className="cls-student-info">
                      <span className="cls-student-name">{s.username}</span>
                      <span className="cls-student-meta">
                        Level {s.level ?? "?"} ·{" "}
                        {s.pending_hw > 0 && <span className="cls-hw-pending">{s.pending_hw} pending</span>}
                        {s.overdue_hw > 0 && <span className="cls-hw-overdue"> {s.overdue_hw} overdue</span>}
                        {s.pending_hw === 0 && s.overdue_hw === 0 && <span>{s.completed_hw} completed</span>}
                      </span>
                    </div>
                    <div className="cls-student-actions">
                      <button
                        className="cls-btn cls-btn--ghost cls-btn--sm"
                        onClick={() => handleDisconnect(s.student_id)}
                        title="Remove student"
                      >
                        <UserX size={15} />
                      </button>
                      <button
                        className="cls-btn cls-btn--primary cls-btn--sm"
                        onClick={() => navigate(`/my-class/student/${s.student_id}`)}
                      >
                        View <ChevronRight size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tab: Requests */}
        {tab === "requests" && (
          <div className="cls-panel">
            {requests.length === 0 ? (
              <div className="cls-empty">
                <UserCheck size={40} className="cls-empty-icon" />
                <p>No pending connection requests.</p>
              </div>
            ) : (
              <ul className="cls-request-list">
                {requests.map(r => (
                  <li key={r.id} className="cls-request-row">
                    <div className="cls-request-info">
                      <span className="cls-student-name">{r.username}</span>
                      <span className="cls-student-meta">Requested {fmtDate(r.requested_at)}</span>
                    </div>
                    <div className="cls-student-actions">
                      <button
                        className="cls-btn cls-btn--danger cls-btn--sm"
                        onClick={() => handleDecline(r.id)}
                      >
                        Decline
                      </button>
                      <button
                        className="cls-btn cls-btn--success cls-btn--sm"
                        onClick={() => handleAccept(r.id)}
                      >
                        Accept
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tab: Notifications */}
        {tab === "notifications" && (
          <div className="cls-panel">
            {notifications.length > 0 && unread > 0 && (
              <div className="cls-notif-toolbar">
                <button className="cls-btn cls-btn--ghost cls-btn--sm" onClick={handleMarkAllRead}>
                  Mark all as read
                </button>
              </div>
            )}
            {notifications.length === 0 ? (
              <div className="cls-empty">
                <Bell size={40} className="cls-empty-icon" />
                <p>No notifications yet.</p>
              </div>
            ) : (
              <ul className="cls-notif-list">
                {notifications.map(n => (
                  <li key={n.id} className={`cls-notif-row${n.is_read ? "" : " cls-notif-row--unread"}`}>
                    <div className="cls-notif-dot" />
                    <div className="cls-notif-body">
                      <p className="cls-notif-msg">{n.message}</p>
                      <p className="cls-notif-time">{fmtDate(n.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
